/**
 * What an interval actually costs, derived from the catalog.
 *
 * The API publishes two amounts per plan — a monthly price and an annual one — and
 * every figure the pricing copy shows is arithmetic on those two. That arithmetic
 * lives here rather than inline in three pages because the pages have to agree: the
 * landing card, the pricing page and the in-portal upgrade panel all quote the same
 * plan, and a per-month equivalent computed one way on one page and another way on
 * the next is a discrepancy a customer reads as a bait-and-switch.
 *
 * Nothing here is a source of truth. Stripe charges `chargedCents`; the rest exists
 * so the page can say what that works out to per month and what it saves.
 */

import type { BillingInterval, Plan } from './types'

export interface PlanPricing {
  /** What Stripe charges per period, per seat: the annual total on `annual`. */
  chargedCents: number
  /** What one month works out to under this interval. */
  perMonthCents: number
  /** The month-to-month price, shown as the figure the annual one is measured against. */
  monthlyCents: number
  /** Twelve monthly payments — what a year costs without committing. */
  yearAtMonthlyCents: number
  /** Saved over a year by paying annually. Zero on the monthly interval. */
  savingPerYearCents: number
  /** Saved each month by paying annually. Zero on the monthly interval. */
  savingPerMonthCents: number
  /** The annual discount as a whole percent. */
  discountPercent: number
}

/**
 * Returns the derived figures for one plan at one interval, or null when the catalog
 * is missing an amount — a plan the API stopped pricing should render as `—` rather
 * than as a confident $0.
 *
 * `statedDiscountPercent` is the figure the API published, and it wins over the ratio
 * of the two amounts so the percentage on the page is the one the amounts were built
 * from. The ratio is only a fallback for an API revision that does not send it.
 */
export function planPricing(
  plan: Plan,
  interval: BillingInterval,
  statedDiscountPercent?: number,
): PlanPricing | null {
  const monthly = plan.prices.find((price) => price.interval === 'monthly')?.cents
  const annual = plan.prices.find((price) => price.interval === 'annual')?.cents
  const charged = interval === 'annual' ? annual : monthly
  if (charged === undefined) return null

  // With no monthly amount to compare against there is no saving to claim, so the
  // annual price stands alone rather than being measured against a guess.
  const monthlyCents = monthly ?? 0
  const yearAtMonthly = monthlyCents * 12
  const annualSaving =
    interval === 'annual' && monthly !== undefined && annual !== undefined
      ? Math.max(0, yearAtMonthly - annual)
      : 0

  return {
    chargedCents: charged,
    perMonthCents: interval === 'annual' ? Math.round(charged / 12) : charged,
    monthlyCents,
    yearAtMonthlyCents: yearAtMonthly,
    savingPerYearCents: annualSaving,
    savingPerMonthCents: Math.round(annualSaving / 12),
    discountPercent:
      statedDiscountPercent ??
      (yearAtMonthly > 0 && annual !== undefined
        ? Math.round(((yearAtMonthly - annual) / yearAtMonthly) * 100)
        : 0),
  }
}
